import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Building, User, Calendar, DollarSign, FileText, CheckCircle2, Search, X, Sparkles } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';

export default function ManualInvoiceModal({ 
  invoice, 
  clients = [], 
  rentals = [], 
  settings = {}, 
  onSave, 
  onClose,
  existingCount = 0 
}) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';
  const cur = settings?.currency || 'MAD';

  // Generate a clean sequential or timestamped invoice number
  const defaultInvoiceNumber = useMemo(() => {
    const year = new Date().getFullYear();
    const seq = String(existingCount + 1).padStart(4, '0');
    return `FAC-${year}-${seq}`;
  }, [existingCount]);

  const defaultDueDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  }, []);

  const [formData, setFormData] = useState(() => {
    if (invoice) {
      return {
        ...invoice,
        isManual: true,
        items: invoice.items && invoice.items.length > 0 ? invoice.items : [
          { id: 'item_1', description: '', quantity: 1, unitPrice: 0, tvaPercent: 20, discountPercent: 0 }
        ]
      };
    }
    return {
      isManual: true,
      invoiceNumber: defaultInvoiceNumber,
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: defaultDueDate,
      status: 'issued', // 'draft' | 'issued' | 'paid' | 'partial' | 'unpaid'
      
      // Selected Client / Company
      clientId: '',
      companyName: '',
      companyAddress: '',
      companyPhone: '',
      companyEmail: '',
      companyICE: '',
      companyIF: '',
      companyRC: '',
      companyCin: '',
      
      // Line items
      items: [
        { id: 'item_1', description: '', quantity: 1, unitPrice: 0, tvaPercent: 20, discountPercent: 0 }
      ],
      
      // Payment details
      paymentMethod: 'Virement', // 'Espèces' | 'Chèque' | 'Virement' | 'Carte' | 'Autre'
      paidAmount: 0,
      notes: ''
    };
  });

  const [companySearch, setCompanySearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Combine unique clients and companies from clients list and past rentals
  const availableCompanies = useMemo(() => {
    const map = new Map();

    // From clients collection
    clients.forEach(c => {
      if (!c) return;
      const key = (c.id || c.name || '').trim();
      if (!key) return;
      map.set(key, {
        id: c.id,
        name: c.name || '',
        phone: c.phone || '',
        email: c.email || '',
        address: c.address || '',
        cin: c.cin || c.passport || '',
        ice: c.ice || c.companyICE || '',
        if: c.if || c.companyIF || '',
        rc: c.rc || c.companyRC || '',
        type: 'client'
      });
    });

    // From rentals collection (in case client was only in rentals)
    rentals.forEach(r => {
      if (!r || !r.clientName) return;
      const name = r.clientName.trim();
      if (!map.has(name) && !map.has(r.clientId)) {
        map.set(name, {
          id: r.clientId || name,
          name: name,
          phone: r.clientPhone || '',
          email: r.clientEmail || '',
          address: r.clientAddress || '',
          cin: r.clientCin || r.clientPassport || '',
          ice: r.clientICE || '',
          if: r.clientIF || '',
          rc: r.clientRC || '',
          type: 'rental_client'
        });
      }
    });

    return Array.from(map.values());
  }, [clients, rentals]);

  // Filtered companies based on search
  const filteredCompanies = useMemo(() => {
    if (!companySearch.trim()) return availableCompanies.slice(0, 15);
    const q = companySearch.toLowerCase();
    return availableCompanies.filter(c => 
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').includes(q) ||
      (c.cin || '').toLowerCase().includes(q) ||
      (c.ice || '').toLowerCase().includes(q)
    ).slice(0, 15);
  }, [availableCompanies, companySearch]);

  const handleSelectCompany = (comp) => {
    setFormData(prev => ({
      ...prev,
      clientId: comp.id || '',
      companyName: comp.name || '',
      companyPhone: comp.phone || '',
      companyEmail: comp.email || '',
      companyAddress: comp.address || '',
      companyCin: comp.cin || '',
      companyICE: comp.ice || '',
      companyIF: comp.if || '',
      companyRC: comp.rc || ''
    }));
    setCompanySearch('');
    setShowDropdown(false);
  };

  // Line item manipulation
  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: `item_${Date.now()}`,
          description: '',
          quantity: 1,
          unitPrice: 0,
          tvaPercent: 20,
          discountPercent: 0
        }
      ]
    }));
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, idx) => idx !== index)
    }));
  };

  const handleItemChange = (index, field, val) => {
    setFormData(prev => {
      const updated = [...prev.items];
      updated[index] = {
        ...updated[index],
        [field]: field === 'description' ? val : Number(val) || 0
      };
      return { ...prev, items: updated };
    });
  };

  // Quick preset items
  const applyPreset = (title, price = 0, tva = 20) => {
    const emptyIndex = formData.items.findIndex(it => !it.description.trim());
    if (emptyIndex !== -1) {
      handleItemChange(emptyIndex, 'description', title);
      if (price > 0) handleItemChange(emptyIndex, 'unitPrice', price);
      handleItemChange(emptyIndex, 'tvaPercent', tva);
    } else {
      setFormData(prev => ({
        ...prev,
        items: [
          ...prev.items,
          {
            id: `item_${Date.now()}`,
            description: title,
            quantity: 1,
            unitPrice: price,
            tvaPercent: tva,
            discountPercent: 0
          }
        ]
      }));
    }
  };

  // Calculations
  const calculatedTotals = useMemo(() => {
    let subtotalHT = 0;
    let totalDiscount = 0;
    let totalTVA = 0;

    formData.items.forEach(it => {
      const qty = Number(it.quantity) || 0;
      const unit = Number(it.unitPrice) || 0;
      const discPct = Number(it.discountPercent) || 0;
      const tvaPct = Number(it.tvaPercent) || 0;

      const lineHT = qty * unit;
      const lineDisc = lineHT * (discPct / 100);
      const lineNetHT = lineHT - lineDisc;
      const lineTVA = lineNetHT * (tvaPct / 100);

      subtotalHT += lineHT;
      totalDiscount += lineDisc;
      totalTVA += lineTVA;
    });

    const totalTTC = Math.max(0, subtotalHT - totalDiscount + totalTVA);
    const paid = Number(formData.paidAmount) || 0;
    const remaining = Math.max(0, totalTTC - paid);

    return {
      subtotalHT,
      totalDiscount,
      totalTVA,
      totalTTC,
      paid,
      remaining
    };
  }, [formData.items, formData.paidAmount]);

  // Handle quick paid buttons (0%, 50%, 100%)
  const setQuickPaid = (ratio) => {
    const amt = Math.round(calculatedTotals.totalTTC * ratio * 100) / 100;
    setFormData(prev => {
      let nextStatus = prev.status;
      if (ratio === 1) nextStatus = 'paid';
      else if (ratio > 0) nextStatus = 'partial';
      else if (prev.status === 'paid' || prev.status === 'partial') nextStatus = 'issued';

      return {
        ...prev,
        paidAmount: amt,
        status: nextStatus
      };
    });
  };

  // Submit
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.companyName.trim()) {
      return alert(isAr ? 'يرجى إدخال اسم الشركة أو الزبون' : 'Veuillez renseigner le nom de la société ou du client.');
    }
    if (!formData.invoiceNumber.trim()) {
      return alert(isAr ? 'يرجى إدخال رقم الفاتورة' : 'Veuillez renseigner le numéro de facture.');
    }
    const hasValidItem = formData.items.some(it => it.description.trim() && it.unitPrice >= 0);
    if (!hasValidItem) {
      return alert(isAr ? 'يرجى إضافة سطر فاتورة واحد على الأقل مع البيان والسعر' : 'Veuillez ajouter au moins une ligne avec une description et un prix.');
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        isManual: true,
        subtotalHT: calculatedTotals.subtotalHT,
        discountAmount: calculatedTotals.totalDiscount,
        tvaAmount: calculatedTotals.totalTVA,
        totalTTC: calculatedTotals.totalTTC,
        paidAmount: calculatedTotals.paid,
        remainingAmount: calculatedTotals.remaining,
        totalAmount: calculatedTotals.totalTTC, // for compatibility
        clientName: formData.companyName, // for unified table view
        updatedAt: new Date().toISOString()
      };

      await onSave(payload);
      onClose();
    } catch (err) {
      alert('❌ ' + (err.message || 'Erreur lors de la sauvegarde'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title={invoice ? (isAr ? 'تعديل الفاتورة' : 'Modifier la facture') : (isAr ? 'إنشاء فاتورة يدويًا' : 'Créer une facture manuellement')}>
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* SECTION 1: SÉLECTION DE LA SOCIÉTÉ / CLIENT */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4.5 sm:p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Building className="w-4.5 h-4.5 text-blue-600" />
              {isAr ? '1. اختيار الشركة / الزبون' : '1. Sélection de la société / Client'}
            </h3>
            {formData.companyName && (
              <span className="text-xs font-semibold px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {isAr ? 'محدد' : 'Sélectionné'}
              </span>
            )}
          </div>

          {/* Searchable dropdown trigger */}
          <div className="relative">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              {isAr ? 'البحث واختيار من الزبائن / الشركات المسجلة' : 'Rechercher parmi les clients / sociétés'}
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={isAr ? 'اكتب اسم الشركة، الزبون، الهاتف أو ICE...' : 'Tapez le nom, téléphone, CIN ou ICE...'}
                value={companySearch}
                onChange={(e) => {
                  setCompanySearch(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                className="w-full pl-9 pr-4 py-2.5 bg-white text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
              />
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
              {companySearch && (
                <button
                  type="button"
                  onClick={() => { setCompanySearch(''); setShowDropdown(false); }}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Dropdown list */}
            {showDropdown && filteredCompanies.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto divide-y divide-slate-100">
                {filteredCompanies.map(c => (
                  <button
                    key={c.id || c.name}
                    type="button"
                    onClick={() => handleSelectCompany(c)}
                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50/80 transition-colors flex items-center justify-between group"
                  >
                    <div>
                      <div className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {c.name}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        {c.phone && <span>📞 {c.phone}</span>}
                        {c.ice && <span className="font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">ICE: {c.ice}</span>}
                        {c.cin && <span>CIN: {c.cin}</span>}
                      </div>
                    </div>
                    <span className="text-xs text-blue-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      {isAr ? 'اختيار ←' : 'Choisir →'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Auto-filled & Editable Company Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 pt-2">
            <div>
              <Field
                label={isAr ? 'اسم الشركة / الزبون *' : 'Nom de la société / Client *'}
                value={formData.companyName}
                onChange={v => setFormData(prev => ({ ...prev, companyName: v }))}
                placeholder={isAr ? 'مثال: شركة النور ش.م.م' : 'Ex: Société ABC SARL'}
                className="px-3.5 py-2 text-sm font-semibold"
              />
            </div>
            <div>
              <Field
                label={isAr ? 'رقم الهاتف' : 'Téléphone'}
                value={formData.companyPhone}
                onChange={v => setFormData(prev => ({ ...prev, companyPhone: v }))}
                placeholder="06XXXXXXXX"
                className="px-3.5 py-2 text-sm"
              />
            </div>
            <div>
              <Field
                label={isAr ? 'البريد الإلكتروني' : 'Email'}
                value={formData.companyEmail}
                onChange={v => setFormData(prev => ({ ...prev, companyEmail: v }))}
                placeholder="contact@societe.com"
                className="px-3.5 py-2 text-sm"
              />
            </div>
            <div className="sm:col-span-2 md:col-span-3">
              <Field
                label={isAr ? 'العنوان' : 'Adresse'}
                value={formData.companyAddress}
                onChange={v => setFormData(prev => ({ ...prev, companyAddress: v }))}
                placeholder={isAr ? 'العنوان الكامل للشركة أو الزبون' : 'Adresse complète de facturation'}
                className="px-3.5 py-2 text-sm"
              />
            </div>
            <div>
              <Field
                label="ICE"
                value={formData.companyICE}
                onChange={v => setFormData(prev => ({ ...prev, companyICE: v }))}
                placeholder="00XXXXXXXXXXXXX"
                className="px-3.5 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <Field
                label="IF (Identifiant Fiscal)"
                value={formData.companyIF}
                onChange={v => setFormData(prev => ({ ...prev, companyIF: v }))}
                placeholder="XXXXXXXX"
                className="px-3.5 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <Field
                label="RC (Registre de Commerce)"
                value={formData.companyRC}
                onChange={v => setFormData(prev => ({ ...prev, companyRC: v }))}
                placeholder="XXXXX"
                className="px-3.5 py-2 text-sm font-mono"
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: INFORMATIONS DE LA FACTURE */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4.5 sm:p-5 space-y-4">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-4.5 h-4.5 text-indigo-600" />
            {isAr ? '2. معلومات الفاتورة' : '2. Informations de la facture'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Field
                label={isAr ? 'رقم الفاتورة *' : 'Numéro de facture *'}
                value={formData.invoiceNumber}
                onChange={v => setFormData(prev => ({ ...prev, invoiceNumber: v }))}
                placeholder="FAC-2026-0001"
                className="px-3.5 py-2 text-sm font-bold font-mono text-blue-700 bg-white"
              />
            </div>
            <div>
              <Field
                label={isAr ? 'تاريخ الفاتورة' : 'Date de facture'}
                type="date"
                value={formData.invoiceDate}
                onChange={v => setFormData(prev => ({ ...prev, invoiceDate: v }))}
                className="px-3.5 py-2 text-sm bg-white"
              />
            </div>
            <div>
              <Field
                label={isAr ? 'تاريخ الاستحقاق' : 'Date d\'échéance'}
                type="date"
                value={formData.dueDate}
                onChange={v => setFormData(prev => ({ ...prev, dueDate: v }))}
                className="px-3.5 py-2 text-sm bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {isAr ? 'الحالة' : 'Statut'}
              </label>
              <select
                value={formData.status}
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3.5 py-2 bg-white text-sm font-semibold border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              >
                <option value="draft">{isAr ? '📝 مسودة (Brouillon)' : '📝 Brouillon'}</option>
                <option value="issued">{isAr ? '📤 صادرة (Émise)' : '📤 Émise'}</option>
                <option value="paid">{isAr ? '✅ مدفوعة بالكامل (Payée)' : '✅ Payée'}</option>
                <option value="partial">{isAr ? '⏳ مدفوعة جزئياً (Partielle)' : '⏳ Partiellement payée'}</option>
                <option value="unpaid">{isAr ? '⚠️ غير مدفوعة (Impayée)' : '⚠️ Impayée'}</option>
              </select>
            </div>
          </div>
        </div>

        {/* SECTION 3: LIGNES DE FACTURE */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4.5 sm:p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-4.5 h-4.5 text-emerald-600" />
              {isAr ? '3. أسطر الفاتورة' : '3. Lignes de facture'}
            </h3>
            
            {/* Quick Presets */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-slate-500 flex items-center gap-1 mr-1">
                <Sparkles className="w-3 h-3 text-amber-500" /> {isAr ? 'إدراج سريع:' : 'Rapide:'}
              </span>
              <button
                type="button"
                onClick={() => applyPreset(isAr ? 'كراء سيارة' : 'Location de véhicule', 350, 20)}
                className="px-2 py-1 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 transition"
              >
                🚗 {isAr ? 'كراء سيارة' : 'Location'}
              </button>
              <button
                type="button"
                onClick={() => applyPreset(isAr ? 'خدمة السائق' : 'Frais de chauffeur', 200, 20)}
                className="px-2 py-1 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 transition"
              >
                👤 {isAr ? 'سائق' : 'Chauffeur'}
              </button>
              <button
                type="button"
                onClick={() => applyPreset(isAr ? 'وقود' : 'Carburant', 150, 10)}
                className="px-2 py-1 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 transition"
              >
                ⛽ {isAr ? 'وقود' : 'Carburant'}
              </button>
              <button
                type="button"
                onClick={() => applyPreset(isAr ? 'غسيل وتنظيف' : 'Lavage & Nettoyage', 80, 20)}
                className="px-2 py-1 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 transition"
              >
                🧽 {isAr ? 'غسيل' : 'Lavage'}
              </button>
            </div>
          </div>

          {/* Table / Lines List */}
          <div className="space-y-3">
            {formData.items.map((item, idx) => {
              const qty = Number(item.quantity) || 0;
              const unit = Number(item.unitPrice) || 0;
              const disc = Number(item.discountPercent) || 0;
              const tva = Number(item.tvaPercent) || 0;
              
              const netHT = (qty * unit) * (1 - disc / 100);
              const lineTTC = netHT * (1 + tva / 100);

              return (
                <div key={item.id || idx} className="bg-white border border-slate-200 rounded-xl p-3.5 shadow-sm space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    
                    {/* Description */}
                    <div className="md:col-span-5">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">
                        {isAr ? `البيان ${idx + 1}` : `Description ${idx + 1}`}
                      </label>
                      <input
                        type="text"
                        placeholder={isAr ? 'مثال: كراء سيارة داسيا لوغان (3 أيام)' : 'Ex: Location Renault Clio (5 jours)'}
                        value={item.description}
                        onChange={e => handleItemChange(idx, 'description', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    {/* Quantity */}
                    <div className="md:col-span-1">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">
                        {isAr ? 'الكمية' : 'Qté'}
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={item.quantity}
                        onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                        className="w-full px-2.5 py-2 text-sm text-center border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold"
                      />
                    </div>

                    {/* Unit Price */}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">
                        {isAr ? `السعر HT (${cur})` : `Prix unit. HT (${cur})`}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={e => handleItemChange(idx, 'unitPrice', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold"
                      />
                    </div>

                    {/* TVA */}
                    <div className="md:col-span-1">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">
                        TVA %
                      </label>
                      <select
                        value={item.tvaPercent}
                        onChange={e => handleItemChange(idx, 'tvaPercent', e.target.value)}
                        className="w-full px-1.5 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-semibold"
                      >
                        <option value="20">20%</option>
                        <option value="14">14%</option>
                        <option value="10">10%</option>
                        <option value="7">7%</option>
                        <option value="0">0%</option>
                      </select>
                    </div>

                    {/* Remise */}
                    <div className="md:col-span-1">
                      <label className="block text-xs font-semibold text-slate-600 mb-1">
                        {isAr ? 'خصم %' : 'Rem. %'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={item.discountPercent}
                        onChange={e => handleItemChange(idx, 'discountPercent', e.target.value)}
                        className="w-full px-2 py-2 text-sm text-center border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    {/* Line Total & Delete */}
                    <div className="md:col-span-2 flex items-center justify-between gap-2">
                      <div className="flex-1 text-right">
                        <span className="block text-[10px] text-slate-400 uppercase font-bold">Total TTC</span>
                        <span className="font-mono font-bold text-slate-900 text-sm">
                          {lineTTC.toFixed(2)} {cur}
                        </span>
                      </div>
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(idx)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title={isAr ? 'حذف السطر' : 'Supprimer la ligne'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleAddItem}
            className="w-full py-2.5 border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/50 text-blue-700 text-sm font-bold rounded-xl transition flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {isAr ? '+ إضافة سطر جديد' : '+ Ajouter une ligne'}
          </button>
        </div>

        {/* SECTION 4 & 5: TOTAUX AUTOMATIQUES & PAIEMENT */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* Payment Details */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4.5 space-y-3.5">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-4.5 h-4.5 text-blue-600" />
              {isAr ? '4. طريقة الأداء والتسديد' : '4. Mode de règlement & Paiement'}
            </h3>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {isAr ? 'طريقة الأداء' : 'Mode de paiement'}
              </label>
              <select
                value={formData.paymentMethod}
                onChange={e => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                className="w-full px-3.5 py-2 bg-white text-sm font-semibold border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              >
                <option value="Espèces">{isAr ? '💵 نقداً (Espèces)' : '💵 Espèces'}</option>
                <option value="Chèque">{isAr ? '📄 شيك (Chèque)' : '📄 Chèque'}</option>
                <option value="Virement">{isAr ? '🏦 تحويل بنكي (Virement)' : '🏦 Virement bancaire'}</option>
                <option value="Carte">{isAr ? '💳 بطاقة بنكية (Carte)' : '💳 Carte bancaire'}</option>
                <option value="Autre">{isAr ? '🏷️ أخرى (Autre)' : '🏷️ Autre'}</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-slate-700">
                  {isAr ? 'المبلغ المؤدى (تسديد كلي أو جزئي)' : 'Montant payé (Partiel / Total)'}
                </label>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setQuickPaid(0)}
                    className="text-[11px] px-2 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded font-semibold"
                  >
                    0%
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickPaid(0.5)}
                    className="text-[11px] px-2 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded font-semibold"
                  >
                    50%
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickPaid(1)}
                    className="text-[11px] px-2 py-0.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded font-semibold"
                  >
                    100%
                  </button>
                </div>
              </div>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.paidAmount}
                  onChange={e => {
                    const val = Number(e.target.value) || 0;
                    setFormData(prev => {
                      let nextStatus = prev.status;
                      if (val >= calculatedTotals.totalTTC && calculatedTotals.totalTTC > 0) nextStatus = 'paid';
                      else if (val > 0) nextStatus = 'partial';
                      else if (prev.status === 'paid' || prev.status === 'partial') nextStatus = 'issued';

                      return {
                        ...prev,
                        paidAmount: val,
                        status: nextStatus
                      };
                    });
                  }}
                  className="w-full px-3.5 py-2 bg-white text-sm font-bold text-emerald-700 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute right-3 top-2.5 text-xs font-bold text-slate-400">{cur}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {isAr ? 'ملاحظات وشروط الفاتورة' : 'Conditions & Notes'}
              </label>
              <textarea
                value={formData.notes}
                onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={isAr ? 'أدخل أي شروط دفع، رقم الشيك، أو تفاصيل إضافية...' : 'Conditions particulières, références chèque/virement...'}
                rows={2}
                className="w-full px-3.5 py-2 bg-white text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Automatic Totals Card */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-5 shadow-lg flex flex-col justify-between space-y-4">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 border-b border-slate-700/60 pb-2">
                {isAr ? 'الحسابات والملخص المالي' : 'Récapitulatif Financier'}
              </h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>{isAr ? 'المجموع دون ضريبة (HT)' : 'Sous-total HT'}:</span>
                  <span className="font-mono font-semibold">{calculatedTotals.subtotalHT.toFixed(2)} {cur}</span>
                </div>
                
                {calculatedTotals.totalDiscount > 0 && (
                  <div className="flex justify-between text-red-400">
                    <span>{isAr ? 'إجمالي الخصم' : 'Remise globale'}:</span>
                    <span className="font-mono font-semibold">-{calculatedTotals.totalDiscount.toFixed(2)} {cur}</span>
                  </div>
                )}

                <div className="flex justify-between text-slate-300">
                  <span>{isAr ? 'مجموع الضريبة (TVA)' : 'Total TVA'}:</span>
                  <span className="font-mono font-semibold">+{calculatedTotals.totalTVA.toFixed(2)} {cur}</span>
                </div>

                <div className="pt-2 border-t border-slate-700 flex justify-between items-baseline text-base font-bold text-white">
                  <span>{isAr ? 'المجموع الصافي (TTC)' : 'Total TTC'}:</span>
                  <span className="font-mono text-xl text-blue-400">{calculatedTotals.totalTTC.toFixed(2)} {cur}</span>
                </div>

                <div className="flex justify-between text-emerald-400 text-sm font-semibold pt-1">
                  <span>{isAr ? 'المبلغ المؤدى' : 'Montant payé'}:</span>
                  <span className="font-mono">{calculatedTotals.paid.toFixed(2)} {cur}</span>
                </div>

                <div className={`flex justify-between text-base font-bold pt-2 border-t border-slate-700/80 ${calculatedTotals.remaining > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  <span>{isAr ? 'المتبقي للأداء' : 'Reste à payer'}:</span>
                  <span className="font-mono">{calculatedTotals.remaining.toFixed(2)} {cur}</span>
                </div>
              </div>
            </div>

            <div className="text-[11px] text-slate-400 text-center">
              {isAr ? 'يتم حفظ الفاتورة تلقائياً وربطها بالشركة/الزبون في قاعدة البيانات.' : 'La facture sera liée à la société et enregistrée de manière permanente.'}
            </div>
          </div>

        </div>

        {/* ACTION BUTTONS */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl text-sm font-bold transition"
          >
            {isAr ? 'إلغاء' : 'Annuler'}
          </button>

          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition shadow-md shadow-blue-500/20 flex items-center gap-2 disabled:opacity-50"
          >
            {submitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <CheckCircle2 className="w-4.5 h-4.5" />
            )}
            {invoice ? (isAr ? 'حفظ التعديلات' : 'Enregistrer les modifications') : (isAr ? 'حفظ الفاتورة' : 'Enregistrer la facture')}
          </button>
        </div>

      </form>
    </Modal>
  );
}
